/**
 * A trilha administrativa (T-15) em frases. Só **conta** o que a API gravou: quem decidiu o
 * que entra (e que segredo nunca entra) é o servidor, em `plataforma.auditoria`.
 */
import type { RegistroAdministrativo } from "../api/tipos";

export const ASSUNTOS = [
  { chave: "", rotulo: "Tudo" },
  { chave: "usuario", rotulo: "Usuários" },
  { chave: "chave", rotulo: "Chaves de pórtico" },
  { chave: "configuracao", rotulo: "Configuração" },
  { chave: "senatran", rotulo: "SENATRAN" },
  { chave: "fila", rotulo: "Exportações" },
  { chave: "demonstracao", rotulo: "Demonstração" },
  { chave: "whatsbi", rotulo: "WhatsBI" },
  { chave: "poder_concedente", rotulo: "Poder concedente" },
  { chave: "notificacoes", rotulo: "Notificações" },
] as const;

type Detalhe = Record<string, unknown>;

const texto = (v: unknown): string =>
  v === null || v === undefined ? "—" : String(v);

function antesDepois(
  d: Detalhe,
  campo: string,
): { antes: unknown; depois: unknown } | null {
  const valor = d[campo];
  return valor && typeof valor === "object" && "depois" in valor
    ? (valor as { antes: unknown; depois: unknown })
    : null;
}

/** O sistema que recebe as notificações por webhook (`R-08`). */
const sistema = (v: unknown): string =>
  v === "OSA"
    ? "a operadora da TAG (OSA)"
    : v === "SUGESTAO_AUTUACAO"
      ? "a Sugestão de Autuação"
      : texto(v);

const situacaoAnterior = (v: unknown): string =>
  v === "SEM_DESTINO"
    ? "estava sem endereço cadastrado"
    : "não tinha sido entregue";

/** O código do perfil, ou "nenhum": o perfil nulo é a concessão sem perfil (`PC-01`). */
const perfilDoPoder = (v: unknown): string =>
  v === null || v === undefined ? "nenhum" : String(v);

/** O dia da entrega (`aaaa-mm-dd`) como a pessoa lê, sem passar por fuso (`PC-02`). */
function diaDoRegistro(v: unknown): string {
  const partes = typeof v === "string" ? v.split("-") : [];
  return partes.length === 3
    ? `${partes[2]}/${partes[1]}/${partes[0]}`
    : texto(v);
}

function mudancasDoUsuario(d: Detalhe): string {
  const partes: string[] = [];
  const perfil = antesDepois(d, "perfil");
  if (perfil)
    partes.push(`perfil ${texto(perfil.antes)} → ${texto(perfil.depois)}`);
  const ativo = antesDepois(d, "ativo");
  if (ativo)
    partes.push(ativo.depois ? "reativou o acesso" : "desativou o acesso");
  if (antesDepois(d, "concessoes")) partes.push("mudou as concessões que vê");
  if (d.senha_redefinida) partes.push("redefiniu a senha");
  return partes.join(", ");
}

/** A frase de uma linha da trilha. Ação desconhecida aparece com o código, e não some. */
export function descricaoDaAcao(r: RegistroAdministrativo): string {
  const d = r.detalhe as Detalhe;
  switch (r.acao) {
    case "usuario.criado":
      return `Criou ${texto(d.nome)} (${texto(d.email)}) como ${texto(d.perfil)}`;
    case "usuario.alterado":
      return `Alterou ${texto(d.email)}: ${mudancasDoUsuario(d)}`;
    case "chave.emitida":
      return d.finalidade === "notificacoes"
        ? `Emitiu a chave do sistema de notificação “${texto(d.nome)}”`
        : d.finalidade === "coletor"
          ? `Emitiu a chave do coletor “${texto(d.nome)}”`
          : `Emitiu a chave de pórtico “${texto(d.nome)}”`;
    case "chave.revogada":
      return `Revogou a chave “${texto(d.nome)}”: ${texto(d.motivo)}`;
    case "configuracao.definida":
      return `Definiu ${texto(d.configuracao)} = ${texto(d.valor)}`;
    case "senatran.cadastro_gravado":
      return `Gravou o cadastro SENATRAN da concessão (CNPJ ${texto(d.cnpj)})`;
    case "senatran.chave_girada":
      return `Gerou uma chave nova do webhook (versão ${texto(d.versao)}) — a anterior parou de valer`;
    case "senatran.portico_gravado":
      return "Gravou o cadastro SENATRAN de um pórtico";
    case "senatran.operador_criado":
      return `Cadastrou o operador de pagamento ${texto(d.razao_social)}`;
    case "senatran.operador_alterado":
      return "Alterou um operador de pagamento";
    case "senatran.carga_baixada":
      return `Baixou a carga histórica ${texto(d.arquivo)} (${texto(d.linhas)} linhas)`;
    case "senatran.atualizacao_por_id":
      return `Gerou o arquivo de atualização por ID (${texto(d.no_arquivo)} de ${texto(d.pedidos)} pedidos)`;
    case "fila.exportada":
      return `Exportou a fila (${texto(d.linhas)} trânsitos, ${String(d.formato ?? "csv").toUpperCase()})${d.busca ? ` — busca “${texto(d.busca)}”` : ""}`;
    case "whatsbi.configuracao_gravada":
      return `Gravou o WhatsApp da concessionária (${d.ativo ? "ligado" : "desligado"})`;
    case "whatsbi.telefone_cadastrado":
      return `Autorizou um celular no WhatsBI (final ${texto(d.final_do_numero)})`;
    case "whatsbi.telefone_removido":
      return `Removeu um celular do WhatsBI (final ${texto(d.final_do_numero)})`;
    case "whatsbi.modelo_gravado":
      return "Gravou o modelo de linguagem do WhatsBI";
    case "whatsbi.guarda_gravada": {
      const prazo = antesDepois(d, "guarda_dias");
      return `Mudou a guarda das conversas: ${texto(prazo?.antes)} → ${texto(prazo?.depois)} dias`;
    }
    case "whatsbi.conversas_expurgadas":
      return d.motivo === "pedido_do_titular"
        ? `Expurgou as conversas de ${texto(d.email)} a pedido (${texto(d.mensagens)} mensagens): ${texto(d.justificativa)}`
        : `Expurgou ${texto(d.mensagens)} mensagens e ${texto(d.conversas)} conversas além de ${texto(d.guarda_dias)} dias`;
    case "whatsbi.conversa_lida":
      // `W-15`: abrir a conversa de outra pessoa é acesso a dado dela — quem e qual, sem o
      // conteúdo.
      return `Leu uma conversa do WhatsBI (${d.canal === "whatsapp" ? "WhatsApp" : "tela"}, ${texto(d.mensagens)} mensagens)`;
    case "isento.incluido":
      return `Incluiu ${texto(d.placa)} na lista de isentos (${texto(d.tipologia)}): ${texto(d.fundamento)}`;
    case "isento.removido":
      return `Removeu ${texto(d.placa)} da lista de isentos: ${texto(d.motivo)}`;
    case "isento.planilha_importada":
      return `Importou a planilha de isentos${d.arquivo ? ` ${texto(d.arquivo)}` : ""} (${texto(d.gravadas)} placas gravadas, ${texto(d.problemas)} com problema)`;
    case "notificacoes.planilha_importada":
      return `Importou a planilha de notificações${d.arquivo ? ` ${texto(d.arquivo)}` : ""} (${texto(d.gravadas)} tentativas gravadas, ${texto(d.repetidas)} já registradas, ${texto(d.problemas)} com problema)`;
    case "notificacoes.reenviada":
      return `Reenviou uma notificação que não tinha chegado ao sistema (${situacaoAnterior(d.situacao_anterior)})`;
    case "notificacoes.destino_definido":
      return `Cadastrou o endereço de ${sistema(d.destinatario)} (${texto(d.servidor)})${d.segredo_gerado ? ", com o segredo da assinatura gerado" : ""}`;
    case "notificacoes.segredo_gerado":
      return `Gerou um segredo novo da assinatura para ${sistema(d.destinatario)} — o anterior parou de valer`;
    case "notificacoes.destino_removido":
      return `Tirou o endereço de ${sistema(d.destinatario)} (${texto(d.servidor)})`;
    case "coleta.configurada":
      return d.modo === "BUSCA"
        ? `Passou o pórtico ${texto(d.praca)} à busca pelo coletor (${texto(d.fabricante)}, ${texto(d.servidor)}, código ${texto(d.codigo_no_fabricante)})${d.retomada_recomecou ? " — outro pórtico: a retomada recomeçou do zero" : ""}`
        : `Passou o pórtico ${texto(d.praca)} a entregar as passagens sozinho`;
    case "coleta.retomada_recomecada":
      return `Recomeçou a retomada da coleta do pórtico ${texto(d.praca)} (estava na ${texto(d.de)}): ${texto(d.motivo)}`;
    case "tarifas.cadastradas":
      return `Cadastrou a tabela de tarifas de ${texto(d.concessao)} (${texto(d.linhas)} linhas, desde ${diaDoRegistro(d.vigente_desde)}), encerrando ${texto(d.encerradas)} vigentes: ${texto(d.fonte)}`;
    case "poder_concedente.definido":
      return `Definiu o perfil do poder concedente de ${texto(d.concessao)}: ${perfilDoPoder(d.de)} → ${perfilDoPoder(d.para)}`;
    case "poder_concedente.liberado":
      return `Liberou o perfil ${texto(d.perfil)} para a organização (linha de comando)`;
    case "poder_concedente.retirado":
      return `Retirou o perfil ${texto(d.perfil)} da organização (linha de comando)`;
    case "poder_concedente.entrega_registrada":
      return `Registrou a entrega ${texto(d.nome)} (${texto(d.competencia)}) de ${texto(d.concessao)} em ${diaDoRegistro(d.entregue_em)}${d.protocolo ? `, protocolo ${texto(d.protocolo)}` : ""}`;
    case "poder_concedente.entrega_anulada":
      return `Anulou o registro da entrega ${texto(d.nome)} (${texto(d.competencia)}) de ${texto(d.concessao)}: ${texto(d.motivo)}`;
    case "poder_concedente.entrega_enviada":
      return `Enviou ${texto(d.nome)} (${texto(d.competencia)}) de ${texto(d.concessao)} a ${texto(d.destinatario)} pelo ${texto(d.canal)}${d.simulado ? " (simulado)" : ""}: protocolo ${texto(d.protocolo)}${d.reenvio ? `; reenvio: ${texto(d.reenvio)}` : ""}`;
    case "poder_concedente.envio_falhou":
      return `Tentou enviar ${texto(d.nome)} (${texto(d.competencia)}) de ${texto(d.concessao)} a ${texto(d.destinatario)}, sem recibo: ${texto(d.motivo)}`;
    case "tollvision.regime_atribuido":
      return `Trocou o regime de prazos de ${texto(d.concessao)}: ${texto(d.de)} → ${texto(d.para)} (${texto(d.motivo)})`;
    case "demonstracao.uploads_apagados":
      return `Apagou uploads da demonstração (${texto(d.lotes)} lotes, ${texto(d.imagens)} imagens, ${texto(d.transitos)} trânsitos)`;
    default:
      return r.acao;
  }
}
